// src/pages/IdentityLibraryView.js
import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, AppBar, Toolbar, Button, CircularProgress, Alert, Grid, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';
import axios from 'axios';

// --- Re-used and necessary imports ---
import { ThemeModeContext } from '../theme/themeContext';
import { useTheme } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import FaceCluster from '../components/FaceCluster'; // --- 1. USE the interactive FaceCluster ---

const API_URL = '/api'; //'http://localhost:8000';

const IdentityLibraryView = () => {
    // --- 2. RENAME state to `clusters` for consistency ---
    const [clusters, setClusters] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState(null);

    const themeMode = useContext(ThemeModeContext);
    const theme = useTheme();

    useEffect(() => {
        const fetchIdentities = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${API_URL}/identities`);
                // The API response format is already perfect for our `clusters` state
                setClusters(response.data);
            } catch (error) {
                const errorMsg = error.response?.data?.detail || "Could not fetch saved identities.";
                setMessage({ text: errorMsg, severity: 'error' });
            } finally {
                setIsLoading(false);
            }
        };
        fetchIdentities();
    }, []);

    // --- 3. ADD all the handlers for interactivity ---

    const handleClusterDetailChange = async (clusterId, field, value) => {
        const oldClusters = JSON.parse(JSON.stringify(clusters));
        // Optimistically update UI for responsiveness
        setClusters(prev => prev.map(c => c.id === clusterId ? { ...c, [field]: value } : c));
        
        try {
            await axios.put(`${API_URL}/identities/${clusterId}`, { [field]: value });
        } catch (error) {
            setMessage({ text: "Failed to update identity.", severity: 'error' });
            setClusters(oldClusters); // Revert on API error
        }
    };

    const handleDeleteCluster = async (clusterId) => {
        if (!window.confirm("Are you sure you want to permanently delete this identity and all its faces?")) return;
        
        const oldClusters = JSON.parse(JSON.stringify(clusters));
        setClusters(prev => prev.filter(c => c.id !== clusterId)); // Optimistic UI update

        try {
            await axios.delete(`${API_URL}/identities/${clusterId}`);
            setMessage({ text: "Identity deleted successfully.", severity: 'success' });
        } catch (error) {
            setMessage({ text: "Failed to delete identity.", severity: 'error' });
            setClusters(oldClusters); // Revert
        }
    };

    const handleDeleteFace = async (faceId) => {
        const oldClusters = JSON.parse(JSON.stringify(clusters));
        // Optimistically remove the face from its cluster in the local state
        setClusters(prev => prev.map(c => ({
            ...c,
            faces: c.faces.filter(f => f.face_id !== faceId)
        })));

        try {
            await axios.delete(`${API_URL}/faces/${faceId}`);
        } catch (error) {
            setMessage({ text: "Failed to remove face.", severity: 'error' });
            setClusters(oldClusters); // Revert
        }
    };

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;

        const faceId = draggableId;
        const sourceClusterId = parseInt(source.droppableId, 10);
        const destClusterId = parseInt(destination.droppableId, 10);

        if (sourceClusterId === destClusterId) return; // No change

        const oldClusters = JSON.parse(JSON.stringify(clusters));
        
        // Optimistic UI Update for a snappy user experience
        const sourceCluster = clusters.find(c => c.id === sourceClusterId);
        const destCluster = clusters.find(c => c.id === destClusterId);
        const movedFace = sourceCluster.faces.find(f => f.face_id === faceId);

        if (!movedFace) return;

        const newSourceFaces = sourceCluster.faces.filter(f => f.face_id !== faceId);
        const newDestFaces = [...destCluster.faces];
        newDestFaces.splice(destination.index, 0, movedFace);


        setClusters(prev => prev.map(c => {
            if (c.id === sourceClusterId) return { ...c, faces: newSourceFaces };
            if (c.id === destClusterId) return { ...c, faces: newDestFaces };
            return c;
        }));

        // API Call to make the change permanent
        try {
            await axios.put(`${API_URL}/faces/${faceId}/assign`, { identity_id: destClusterId });
        } catch (error) {
            setMessage({ text: "Failed to move face.", severity: "error" });
            setClusters(oldClusters); // Revert on failure
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
            <AppBar position="static" color="default">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>My Identity Library</Typography>
                    <Button variant="contained" color="secondary" component={Link} to="/custom">Back to Custom Dating</Button>
                    <IconButton sx={{ ml: 1 }} onClick={themeMode.toggleThemeMode} color="inherit">
                        {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                    </IconButton>
                </Toolbar>
            </AppBar>
            
            {/* --- 4. WRAP content in DragDropContext --- */}
            <DragDropContext onDragEnd={onDragEnd}>
                <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
                    {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
                    
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
                    ) : clusters.length > 0 ? (
                        <Grid container spacing={2}>
                            {clusters.map(cluster => (
                                <Grid item key={cluster.id} xs={12} md={6} lg={4}>
                                    {/* --- 5. RENDER the interactive FaceCluster --- */}
                                    <FaceCluster
                                        cluster={cluster}
                                        onRemoveFace={handleDeleteFace}
                                        onBirthYearChange={(id, year) => handleClusterDetailChange(id, 'birth_year', year)}
                                        onNameChange={(id, name) => handleClusterDetailChange(id, 'name', name)}
                                        onRemoveCluster={handleDeleteCluster}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography sx={{ textAlign: 'center', mt: 4 }}>You have no saved identities.</Typography>
                    )}
                </Box>
            </DragDropContext>
        </Box>
    );
};

export default IdentityLibraryView;
